
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "sonner";

import { TooltipProvider } from "./components/ui/tooltip";
import ScrollToTop from "./pages/ScrollToTopButton/ScrollToTop";

// Landing Page
import LandingPage from "./pages/LandingPage";

// Auth pages
import Login from "./pages/auth/Login";
import CandidateLogin from "./pages/auth/CandidateLogin";
import CompanyRegister from "./pages/auth/CompanyRegister";

// Admin layout and pages
import { AdminLayout } from "./components/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import SuperAdminDashboard from "./pages/admin/SuperAdminDashboard";
import Questions from "./pages/admin/Questions";
import QuestionDetails from "./pages/admin/QuestionDetails";
import ExamAssignment from "./pages/admin/ExamAssignment";
import Exams from "./pages/admin/Exams";
import Candidates from "./pages/admin/Candidates";
import Categories from "./pages/admin/Categories";
import QuestionTypes from "./pages/admin/QuestionTypes";
import Analytics from "./pages/admin/Analytics";
import Settings from "./pages/admin/Settings";
import ManageAdmins from "./pages/admin/ManageAdmins";
import CompanyCandidates from "./pages/admin/CompanyCandidates";
import QuestionBankPurchase from "./pages/admin/QuestionBankPurchase";
import ExamCalendar from "./pages/admin/ExamCalendar";
import AIModule from "./pages/admin/AIModule";

// Master layout and pages
import { MasterLayout } from "./components/master/MasterLayout";
import MasterDashboard from "./pages/master/MasterDashboard";
import CompaniesManagement from "./pages/master/CompaniesManagement";
import SystemSettings from "./pages/master/SystemSettings";
import ManageQuestionBanks from "./pages/master/ManageQuestionBanks";
import MasterAnalytics from "./pages/master/MasterAnalytics";
import QuestionBankUpload from "./pages/master/QuestionBankUpload";

// Candidate layout and pages
import { CandidateLayout } from "./components/candidate/CandidateLayout";
import CandidateDashboard from "./pages/candidate/CandidateDashboard";
import ExamInstructions from "./pages/candidate/ExamInstructions";
import ExamScreen from "./pages/candidate/ExamScreen";
import ExamComplete from "./pages/candidate/ExamComplete";

// Payment pages
import PricingPlans from "./pages/payments/PricingPlans";
import CheckoutPage from "./pages/payments/CheckoutPage";
import PaymentSuccess from "./pages/payments/PaymentSuccess";
import PaymentCanceled from "./pages/payments/PaymentCanceled";

// Auth guards
import AdminRoute from "./components/auth/AdminRoute";
import CandidateRoute from "./components/auth/CandidateRoute";
import MasterAdminRoute from "./components/auth/MasterAdminRoute";
import CompanySuperAdminRoute from "./components/auth/CompanySuperAdminRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Static pages
import Features from "./pages/static/Features";
import About from "./pages/static/About";
import Contact from "./pages/static/Contact";
import RequestDemo from "./pages/static/RequestDemo";
import QuestionBanksPage from "./pages/static/QuestionBanksPage";


// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10 * (60 * 1000), // 10 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/landing_page" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/candidate-login" element={<CandidateLogin />} />
              <Route path="/register" element={<CompanyRegister />} />
              <Route path="/pricing" element={<PricingPlans />} />
              
              {/* Static pages */}
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/demo" element={<RequestDemo />} />
              <Route path="/question-banks" element={<QuestionBanksPage />} />
              
              {/* Protected payment routes */}
              <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/payment-canceled" element={<PaymentCanceled />} />

              {/* Master Admin routes */}
              <Route
                path="/master-admin"
                element={
                  <MasterAdminRoute>
                    <MasterLayout />
                  </MasterAdminRoute>
                }
              >
                <Route index element={<MasterDashboard />} />
                <Route path="companies" element={<CompaniesManagement />} />
                <Route path="system-settings" element={<SystemSettings />} />
                <Route path="question-banks" element={<ManageQuestionBanks />} />
                <Route path="question-banks/upload" element={<QuestionBankUpload />} />
                <Route path="question-types" element={<QuestionTypes />} />
                <Route path="analytics" element={<MasterAnalytics />} />
              </Route>

              {/* Company Super Admin routes */}
              <Route
                path="/super-admin"
                element={
                  <CompanySuperAdminRoute>
                    <AdminLayout />
                  </CompanySuperAdminRoute>
                }
              >
                <Route index element={<SuperAdminDashboard />} />
                <Route path="manage-admins" element={<ManageAdmins />} />
                <Route path="candidates" element={<CompanyCandidates />} />
                <Route path="question-banks" element={<QuestionBankPurchase />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              {/* Company Admin routes */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="question_bank" element={<QuestionDetails />} />
                <Route path="question_bank/questions" element={<Questions />} />
                <Route path="exams" element={<Exams />} />
                <Route path="candidates" element={<Candidates />} />
                <Route path="exam_assignment" element={<ExamAssignment />} />
                <Route path="categories" element={<Categories />} />
                {/* <Route path="ai-module" element={<AIModule />} /> */}
                <Route path="analytics" element={<Analytics />} />
                <Route path="settings" element={<Settings />} />
                <Route path="exam_calendar" element={<ExamCalendar />} />
              </Route>

              {/* Candidate routes */}
              <Route
                path="/candidate"
                element={
                  <CandidateRoute>
                    <CandidateLayout />
                  </CandidateRoute>
                }
              >
                <Route index element={<CandidateDashboard />} />
                <Route path="instructions/:assignmentId" element={<ExamInstructions />} />
                <Route path="exam/:sessionId" element={<ExamScreen />} />
                <Route path="exam-complete" element={<ExamComplete />} />
              </Route>

              {/* Catch-all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </TooltipProvider>
            <ScrollToTop />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </QueryClientProvider>


  );
}

export default App;
